import i18n from '../index';

/**
 * Relative time formatting utilities using the Intl.RelativeTimeFormat API.
 * Automatically uses the current i18n locale.
 */

type RelativeTimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';

interface TimeUnit {
  unit: RelativeTimeUnit;
  ms: number;
}

const TIME_UNITS: TimeUnit[] = [
  { unit: 'year', ms: 365 * 24 * 60 * 60 * 1000 },
  { unit: 'month', ms: 30 * 24 * 60 * 60 * 1000 },
  { unit: 'day', ms: 24 * 60 * 60 * 1000 },
  { unit: 'hour', ms: 60 * 60 * 1000 },
  { unit: 'minute', ms: 60 * 1000 },
  { unit: 'second', ms: 1000 },
];

const MS_PER_MINUTE = 60 * 1000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function isYesterdayCalendarDay(dateToCheck: Date): boolean {
  const d = new Date(dateToCheck.getTime());
  d.setHours(0, 0, 0, 0);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  return d.getTime() === yesterday.getTime();
}

function isYesterdayRelativeTo(dateToCheck: Date, referenceNow: Date): boolean {
  const date = new Date(dateToCheck.getTime());
  const today = new Date(referenceNow.getTime());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  yesterday.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date.getTime() === yesterday.getTime();
}

function isTomorrowRelativeTo(dateToCheck: Date, referenceNow: Date): boolean {
  const date = new Date(dateToCheck.getTime());
  const today = new Date(referenceNow.getTime());
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date.getTime() === tomorrow.getTime();
}

function getRelativeTimeStringShortHuman(
  timestamp: Date,
  compareDate: Date,
): string {
  const diffMs = timestamp.getTime() - compareDate.getTime();
  const isFuture = diffMs > 0;
  const diff = Math.abs(diffMs);
  const diffInSeconds = Math.round(diff / 1000);
  const diffInMinutes = Math.round(diffInSeconds / 60);
  const diffInHours = Math.round(diffInMinutes / 60);
  const diffInDays = Math.round(diffInHours / 24);

  if (diffInSeconds < 60) {
    return i18n.t('date:relativeTimeShortHumanJustNow', {
      defaultValue: 'just now',
    });
  }

  if (diffInMinutes < 60) {
    if (isFuture) {
      return i18n.t('date:relativeTimeShortHumanMinutesIn', {
        minutes: diffInMinutes,
        defaultValue: 'in {{minutes}} min.',
      });
    }
    return i18n.t('date:relativeTimeShortHumanMinutesAgo', {
      minutes: diffInMinutes,
      defaultValue: '{{minutes}} min. ago',
    });
  }

  if (diffInHours < 24) {
    if (diffInHours === 1) {
      if (isFuture) {
        return i18n.t('date:relativeTimeShortHumanAnHourIn', {
          defaultValue: 'in an hour',
        });
      }
      return i18n.t('date:relativeTimeShortHumanAnHourAgo', {
        defaultValue: 'an hour ago',
      });
    }
    if (isFuture) {
      return i18n.t('date:relativeTimeShortHumanHoursIn', {
        count: diffInHours,
        defaultValue: 'in {{count}} hours',
      });
    }
    return i18n.t('date:relativeTimeShortHumanHoursAgo', {
      count: diffInHours,
      defaultValue: '{{count}} hours ago',
    });
  }

  if (diffInDays < 30) {
    if (diffInDays === 1) {
      if (isFuture) {
        return i18n.t('date:relativeTimeShortHumanADayIn', {
          defaultValue: 'in a day',
        });
      }
      return i18n.t('date:relativeTimeShortHumanADayAgo', {
        defaultValue: 'a day ago',
      });
    }
    if (isFuture) {
      return i18n.t('date:relativeTimeShortHumanDaysIn', {
        count: diffInDays,
        defaultValue: 'in {{count}} days',
      });
    }
    return i18n.t('date:relativeTimeShortHumanDaysAgo', {
      count: diffInDays,
      defaultValue: '{{count}} days ago',
    });
  }

  if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    if (months === 1) {
      if (isFuture) {
        return i18n.t('date:relativeTimeShortHumanAMonthIn', {
          defaultValue: 'in a month',
        });
      }
      return i18n.t('date:relativeTimeShortHumanAMonthAgo', {
        defaultValue: 'a month ago',
      });
    }
    if (isFuture) {
      return i18n.t('date:relativeTimeShortHumanMonthsIn', {
        count: months,
        defaultValue: 'in {{count}} months',
      });
    }
    return i18n.t('date:relativeTimeShortHumanMonthsAgo', {
      count: months,
      defaultValue: '{{count}} months ago',
    });
  }

  const years = Math.floor(diffInDays / 365);
  if (years === 1) {
    if (isFuture) {
      return i18n.t('date:relativeTimeShortHumanAYearIn', {
        defaultValue: 'in a year',
      });
    }
    return i18n.t('date:relativeTimeShortHumanAYearAgo', {
      defaultValue: 'a year ago',
    });
  }
  if (isFuture) {
    return i18n.t('date:relativeTimeShortHumanYearsIn', {
      count: years,
      defaultValue: 'in {{count}} years',
    });
  }
  return i18n.t('date:relativeTimeShortHumanYearsAgo', {
    count: years,
    defaultValue: '{{count}} years ago',
  });
}

function formatRelativeTimeShortHuman(date: Date, nowMs: number): string {
  const compareDate = new Date(nowMs);
  const diffMs = date.getTime() - compareDate.getTime();
  const isFuture = diffMs > 0;
  const duration = Math.abs(diffMs);
  const asHours = Math.floor(duration / (1000 * 60 * 60));
  const asMinutes = Math.floor(duration / (1000 * 60)) - asHours * 60;

  if (!isFuture && isYesterdayRelativeTo(date, compareDate)) {
    return i18n.t('date:relativeTimeShortHumanADayAgo', {
      defaultValue: 'a day ago',
    });
  }

  if (isFuture && isTomorrowRelativeTo(date, compareDate)) {
    return i18n.t('date:relativeTimeShortHumanADayIn', {
      defaultValue: 'in a day',
    });
  }

  if (asHours > 0 && asHours < 24 && asMinutes < 60 && asMinutes > 0) {
    if (isFuture) {
      if (asHours === 1) {
        return i18n.t('date:relativeTimeShortHumanOneHrMinutesIn', {
          minutes: asMinutes,
          defaultValue: 'in 1 hr. {{minutes}} min.',
        });
      }
      return i18n.t('date:relativeTimeShortHumanManyHrsMinutesIn', {
        hours: asHours,
        minutes: asMinutes,
        defaultValue: 'in {{hours}} hrs. {{minutes}} min.',
      });
    }
    if (asHours === 1) {
      return i18n.t('date:relativeTimeShortHumanOneHrMinutesAgo', {
        minutes: asMinutes,
        defaultValue: '1 hr. {{minutes}} min. ago',
      });
    }
    return i18n.t('date:relativeTimeShortHumanManyHrsMinutesAgo', {
      hours: asHours,
      minutes: asMinutes,
      defaultValue: '{{hours}} hrs. {{minutes}} min. ago',
    });
  }

  return getRelativeTimeStringShortHuman(date, compareDate);
}

function formatCompoundHourPart(hours: number, locale: string): string {
  if (hours === 1) {
    return i18n.t('date:relativeTimeOneHourUnit', {
      defaultValue: 'an hour',
    });
  }
  return new Intl.NumberFormat(locale, {
    style: 'unit',
    unit: 'hour',
    unitDisplay: 'long',
  }).format(hours);
}

function formatPastHoursAndMinutesAgo(
  absDiffMs: number,
  locale: string,
): string | null {
  const hours = Math.floor(absDiffMs / MS_PER_HOUR);
  const minutes = Math.floor((absDiffMs % MS_PER_HOUR) / (60 * 1000));
  if (hours <= 0 || minutes <= 0 || absDiffMs >= MS_PER_DAY) {
    return null;
  }
  try {
    const hourPart = formatCompoundHourPart(hours, locale);
    const minutePart = new Intl.NumberFormat(locale, {
      style: 'unit',
      unit: 'minute',
      unitDisplay: 'long',
    }).format(minutes);
    return i18n.t('date:relativeTimeAgoCompound', {
      hourPart,
      minutePart,
      defaultValue: '{{hourPart}} and {{minutePart}} ago',
    });
  } catch {
    const hourPart =
      hours === 1
        ? 'an hour'
        : new Intl.NumberFormat('en', {
            style: 'unit',
            unit: 'hour',
            unitDisplay: 'long',
          }).format(hours);
    const minutePart =
      minutes === 1
        ? i18n.t('date:relativeTimeOneMinuteUnit', {
            defaultValue: '1 minute',
          })
        : new Intl.NumberFormat('en', {
            style: 'unit',
            unit: 'minute',
            unitDisplay: 'long',
          }).format(minutes);
    return `${hourPart} and ${minutePart} ago`;
  }
}

function formatLongStyleSingleCalendarUnit(
  value: number,
  selectedUnit: RelativeTimeUnit,
): string | null {
  if (
    selectedUnit !== 'hour' &&
    selectedUnit !== 'day' &&
    selectedUnit !== 'month' &&
    selectedUnit !== 'year'
  ) {
    return null;
  }
  if (value === -1) {
    switch (selectedUnit) {
      case 'hour':
        return i18n.t('date:relativeTimeShortHumanAnHourAgo', {
          defaultValue: 'an hour ago',
        });
      case 'day':
        return i18n.t('date:relativeTimeShortHumanADayAgo', {
          defaultValue: 'a day ago',
        });
      case 'month':
        return i18n.t('date:relativeTimeShortHumanAMonthAgo', {
          defaultValue: 'a month ago',
        });
      case 'year':
        return i18n.t('date:relativeTimeShortHumanAYearAgo', {
          defaultValue: 'a year ago',
        });
      default:
        return null;
    }
  }
  if (value === 1) {
    switch (selectedUnit) {
      case 'hour':
        return i18n.t('date:relativeTimeShortHumanAnHourIn', {
          defaultValue: 'in an hour',
        });
      case 'day':
        return i18n.t('date:relativeTimeShortHumanADayIn', {
          defaultValue: 'in a day',
        });
      case 'month':
        return i18n.t('date:relativeTimeShortHumanAMonthIn', {
          defaultValue: 'in a month',
        });
      case 'year':
        return i18n.t('date:relativeTimeShortHumanAYearIn', {
          defaultValue: 'in a year',
        });
      default:
        return null;
    }
  }
  return null;
}

/**
 * Formats a relative time (e.g., "2 days ago", "in 3 hours").
 *
 * @param date - The date to compare to now
 * @param style - The format style ('long', 'short', 'narrow', or 'short-human')
 * @returns Formatted relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 86400000)) // "a day ago" (long style)
 * formatRelativeTime(new Date(Date.now() + 3600000)) // "in 1 hour"
 */
export const formatRelativeTime = (
  date: Date | number | string | null | undefined,
  style: 'long' | 'short' | 'narrow' | 'short-human' = 'long',
): string => {
  if (date === null || date === undefined) return '';
  const dateObj = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(dateObj.getTime())) return '';
  const now = Date.now();
  if (style === 'short-human') {
    return formatRelativeTimeShortHuman(dateObj, now);
  }
  const diff = dateObj.getTime() - now;
  const absDiff = Math.abs(diff);
  const locale = i18n.language || 'en';
  const isPast = diff < 0;

  if (isPast && isYesterdayCalendarDay(dateObj)) {
    if (style === 'long') {
      return i18n.t('date:relativeTimeShortHumanADayAgo', {
        defaultValue: 'a day ago',
      });
    }
    try {
      const rtf = new Intl.RelativeTimeFormat(locale, {
        style,
        numeric: 'auto',
      });
      return rtf.format(-1, 'day');
    } catch {
      const rtf = new Intl.RelativeTimeFormat('en', {
        style,
        numeric: 'auto',
      });
      return rtf.format(-1, 'day');
    }
  }

  if (absDiff < MS_PER_MINUTE) {
    return i18n.t('date:relativeTimeShortHumanJustNow', {
      defaultValue: 'just now',
    });
  }

  // Find the appropriate unit
  let selectedUnit: RelativeTimeUnit = 'second';
  let value = 0;

  for (const { unit, ms } of TIME_UNITS) {
    if (absDiff >= ms) {
      selectedUnit = unit;
      const truncated = Math.floor(absDiff / ms);
      value = diff < 0 ? -truncated : truncated;
      break;
    }
  }

  if (style === 'long' && isPast && selectedUnit === 'hour') {
    const compound = formatPastHoursAndMinutesAgo(absDiff, locale);
    if (compound !== null) {
      return compound;
    }
  }

  if (style === 'long') {
    const longSingleUnit = formatLongStyleSingleCalendarUnit(
      value,
      selectedUnit,
    );
    if (longSingleUnit !== null) {
      return longSingleUnit;
    }
  }

  try {
    const rtf = new Intl.RelativeTimeFormat(locale, {
      style,
      numeric: 'auto',
    });
    return rtf.format(value, selectedUnit);
  } catch {
    const rtf = new Intl.RelativeTimeFormat('en', {
      style,
      numeric: 'auto',
    });
    return rtf.format(value, selectedUnit);
  }
};

/**
 * Formats a duration in a human-readable format.
 * Not used in the app; for call and recording lengths use
 * `formatCallDuration` in `duration.ts`, which joins units per locale.
 *
 * @param ms - Duration in milliseconds
 * @returns Formatted duration string
 *
 * @example
 * formatDuration(3661000) // "1 hour, 1 minute"
 * formatDuration(90000) // "1 minute, 30 seconds"
 */
export const formatDuration = (ms: number): string => {
  const locale = i18n.language || 'en';
  const absDiff = Math.abs(ms);

  const parts: string[] = [];

  let remaining = absDiff;

  for (const { unit, ms: unitMs } of TIME_UNITS) {
    if (remaining >= unitMs) {
      const count = Math.floor(remaining / unitMs);
      remaining %= unitMs;

      try {
        // Use Intl.NumberFormat with unit style for proper pluralization
        const formatter = new Intl.NumberFormat(locale, {
          style: 'unit',
          unit,
          unitDisplay: 'long',
        });
        parts.push(formatter.format(count));
      } catch {
        // Fallback for older browsers
        parts.push(`${count} ${unit}${count !== 1 ? 's' : ''}`);
      }

      // Only show two most significant units
      if (parts.length >= 2) break;
    }
  }

  return parts.join(', ') || '0 seconds';
};

/**
 * Gets a "time ago" string for a date.
 * Alias for formatRelativeTime with simpler API.
 *
 * @param date - The date to format
 * @returns Relative time string
 *
 * @example
 * timeAgo(new Date(Date.now() - 60000)) // "1 minute ago"
 */
export const timeAgo = (date: Date | number | string): string =>
  formatRelativeTime(date, 'long');
